import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigurationStep } from '~/app/compositions/sidebar/configuration-step/configuration-step';
import { OptionsHeader } from '~/app/compositions/sidebar/options-header';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { hasTeaserText, SelectionCardNew } from '~/components/selection-card-new/selection-card-new';
import { useActiveCard } from '~/components/slider-panel';
import { RenderHtml } from '~/components/text';
import { ConfiguredCarCatalogImage } from '~/graphql/__generated__/graphql';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useSteps } from '~/graphql/hooks/use-steps';
import { CatalogItem } from '~/graphql/mapper';
import { FeatureOrPackage, SelectionCardItem } from '~/graphql/types';

export function EquipmentHighlights() {
	const { i18n } = useI18n();
	const { equipmentHighlights } = useCatalog();
	const { isLoading, loadingFlags, isItemLoading, isItemChecked } = useConfigurator();
	const { selectCard } = useActiveCard();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();
	const { getName } = useDebugMode();
	const { mediaByProfileId } = useImages();
	const [imageUrls, setImageUrls] = useState<(string[] | null | undefined)[] | undefined>([]);
	const [media, setMedia] = useState<Array<Array<ConfiguredCarCatalogImage | null | undefined>>>([]);
	const { step, total } = useSteps('equipmentHighlights');

	const trackSelectionClickEvent = useCallback(
		(item: FeatureOrPackage, isSelectedFromLayer: boolean, selected: boolean) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: isSelectedFromLayer ? 'button' : 'card',
				value: isSelectedFromLayer ? 'details layer' : `selection feature app: equipmentHighlights: all`,
				category: 'equipmentHighlights',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const items: SelectionCardItem[] = useMemo(() => {
		if (!equipmentHighlights) return [];

		return equipmentHighlights.map((item, index): SelectionCardItem => {
			return {
				name: getName(item) ?? item.name,
				description: hasTeaserText((item.__item as FeatureOrPackage).texts?.teaserText)
					? (item.__item as FeatureOrPackage).texts?.teaserText
					: null,
				key: item.pr7,
				loading: isItemLoading(item),
				selected: isItemChecked(item),
				readonly: loadingFlags.checkingConflicts || !item.status.available,
				onSelect: () =>
					selectCard(index, item as CatalogItem, () =>
						trackSelectionClickEvent(item.__item as FeatureOrPackage, false, isItemChecked(item)),
					),
				standard: item.status.standard,
				galleryImageUrls: item?.galleryImages || [],
				galleryMedia: item?.galleryMedia || [],
				price: config.showPrices
					? {
							totalPrice: item.totalPrice,
							monthlyRate: !config.hideMonthlyRates ? item.monthlyRate : undefined,
							feature,
						}
					: undefined,
				disclaimerHeadline: item.__item.texts?.disclaimerHeadline || undefined,
				disclaimerContent: item.__item.texts?.disclaimerText || undefined,
				disclaimerStartDate: item.__item.texts?.disclaimerStartDate || undefined,
				disclaimerEndDate: item.__item.texts?.disclaimerEndDate || undefined,
			};
		});
	}, [
		equipmentHighlights,
		getName,
		isItemLoading,
		isItemChecked,
		loadingFlags.checkingConflicts,
		config.showPrices,
		config.hideMonthlyRates,
		feature,
		selectCard,
		trackSelectionClickEvent,
	]);

	useEffect(() => {
		const selectedItems = items?.filter((a) => a.selected);
		const images = selectedItems?.map((a) => a.galleryImageUrls) as string[][];
		const newImages = images?.filter((a) => {
			return !imageUrls?.find((b) => {
				return JSON.stringify(b) === JSON.stringify(a);
			});
		});
		const buffer = structuredClone(imageUrls!).filter((a) =>
			images.find((b) => JSON.stringify(b) === JSON.stringify(a)),
		);

		const structuredMedia: Array<Array<ConfiguredCarCatalogImage | null | undefined>> = [];

		selectedItems?.forEach((itm) => {
			if (Array.isArray(itm.galleryMedia)) {
				structuredMedia.push(itm.galleryMedia);
			} else {
				structuredMedia.push([itm.galleryMedia]);
			}
		});

		setMedia(structuredMedia);

		if (newImages) {
			setImageUrls([...structuredClone(newImages), ...structuredClone(buffer)]);
		}
	}, [JSON.stringify(imageUrls), JSON.stringify(items)]);

	const impressionTrackingRef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading && total) {
				appTrackingService.selectionStepImpression({
					node,
					value: 'equipmentHighlights',
					elementCurrentStep: step,
					elementTotalSteps: total,
				});
			}
		},
		[isLoading],
	);

	const categoryDisclaimer = config.categoryDisclaimers?.find((d) => d.step === 'equipmentHighlights')?.disclaimer;

	return (
		<ConfigurationStep
			ref={impressionTrackingRef}
			hide={items.length === 0}
			id={'equipment-highlights'}
			media={
				media && media.length > 0
					? [...media, [mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]]]
					: [[mediaByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD]]]
			}
			headline={<OptionsHeader title={i18n('ngc.equipment.equipmentHighlights')} step={step} total={total} />}
			disclaimer={categoryDisclaimer}>
			{items.map((item) => (
				<SelectionCardNew key={item.name} item={item}>
					{item.description && <RenderHtml html={item.description} />}
				</SelectionCardNew>
			))}
		</ConfigurationStep>
	);
}
